import { useEffect } from 'react'

import {
  useViewingRoomStore,
  useViewedRoomStore,
  type Room,
  useSidebarStore,
  useSelectedMarkerStore,
  TRoomMarker,
} from '@/entities/map'
import { eventManager, useOverlay } from '@/shared/lib'

import { type RoomOverlay, type RoomOverlayProps, createRoomOverlay } from '.'

import { getContent } from './element'

export const useRoomOverlay = () => {
  const sidebarActions = useSidebarStore(state => state.actions)

  const isViewing = useViewingRoomStore(state => state.isViewing)
  const hasViewed = useViewedRoomStore(state => state.hasViewed)
  const viewingRoomActions = useViewingRoomStore(state => state.actions)

  const addViewedRoom = useViewedRoomStore(state => state.actions.addViewedRoom)

  const resetMarker = useSelectedMarkerStore(state => state.actions.resetMarker)

  const updateSidebar = () => {
    const isSidebarOpen = useSidebarStore.getState().isOpen

    if (!isSidebarOpen) {
      sidebarActions.open()

      setTimeout(() => {
        eventManager.triggerEvent('resize')
      }, 0)
    }
  }

  const updateViewingRoom = (room: Room) => {
    if (isViewing(room.id)) {
      viewingRoomActions.resetViewingRoom()
    } else {
      viewingRoomActions.updateViewingRoom(room)
      addViewedRoom(room.id)
    }
  }

  const handleRoomOverlayClick = (marker: TRoomMarker) => {
    updateViewingRoom({
      id: marker.rid,
      position: { lat: marker.lat, lng: marker.lng },
    })
    updateSidebar()
    resetMarker()
  }

  const roomOverlay = useOverlay<RoomOverlay, RoomOverlayProps, TRoomMarker>({
    createInstance: createRoomOverlay,
    getOverlayOptions: item => ({
      zIndex: 40,
      roomId: item.rid,
      content: getContent({
        hasLike: item.hasLike,
        hasViewed: hasViewed(item.rid),
        fee: item.feePerWeek,
        sameGpsCnt: item.sameGpsCnt,
      }),
      onClick: () => {
        handleRoomOverlayClick(item)
      },
    }),
  })

  const handleViewingRoomChange = () => {
    if (!roomOverlay.ref.current) return

    roomOverlay.ref.current.forEach(overlay => {
      if (overlay.isViewing && !isViewing(overlay.roomId)) {
        overlay.markAsViewed()
      }
    })
  }

  useEffect(
    () =>
      useViewingRoomStore.subscribe(
        state => state.viewingRoom,
        handleViewingRoomChange,
      ),
    [],
  )

  return roomOverlay
}
